import {
	type PartnerAvailabilityUpdateItemDto,
	type PartnerCalendarPartnerDto,
	type PartnerCalendarRequestDto,
	PartnerSkills,
} from "Infrastructure/Api/Api";
import { processResponse } from "Infrastructure/Api/ApiUtils";
import { useMarketRegionList } from "Market/Api/Query/useMarketRegionList";
import { useMarket } from "Market/Providers/CurrentMarketProvider";
import { useBulkUpsertPartnerAvailabilityMutation } from "Partner/Api/Mutation/useBulkUpsertPartnerAvailabilityMutation";
import {
	type PartnerCalendarRequest,
	postPartnerCalendarEmails,
} from "Partner/Api/PartnerCalendarExtensionsApi";
import { usePartnerCalendarQuery } from "Partner/Api/Query/usePartnerCalendarQuery";
import { CalendarGrid } from "Partner/Components/Calendar/CalendarGrid";
import { Legend } from "Partner/Components/Calendar/Legend";
import { PartnerCalendarSkeleton } from "Partner/Components/Calendar/PartnerCalendarSkeleton";
import {
	type PartnerAvailability,
	type PartnerCalendarRowData,
	PartnerCalendarStatus,
} from "Partner/Components/Calendar/types";
import { useVerticalListQuery } from "Settings/Modules/OrderSetup/Api/Query/useVerticalListQuery";
import {
	ChevronLeft,
	ChevronRight,
	ContentCopy,
	Save,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CircularProgress,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TablePagination,
	Typography,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { toast } from "sonner";

/**
 * Converts a SCREAMING_SNAKE_CASE enum value to Title Case for display.
 * e.g. "INTERIOR_PAINTING" → "Interior Painting", "PARQUET_RENO" → "Parquet Reno"
 */
function formatSkillLabel(skill: string): string {
	return skill
		.replace(/_/g, " ") // Replace underscores with spaces
		.toLowerCase() // Convert to lowercase
		.replace(/\b\w/g, (c) => c.toUpperCase()); // Capitalize first letter of each word
}

export function PartnerCalendarWidget() {
	const { market } = useMarket();
	const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const [selectedRegions, setSelectedRegions] = useState<number[]>([]);
	const [selectedVerticals, setSelectedVerticals] = useState<number[]>([]);
	const [selectedSkills, setSelectedSkills] = useState<PartnerSkills[]>([]);
	const [sortDate, setSortDate] = useState<string>();
	const [isCopyingEmails, setIsCopyingEmails] = useState(false);
	const [modifiedAvailability, setModifiedAvailability] = useState<
		Map<string, PartnerCalendarStatus>
	>(new Map());
	const startDate = currentDate.startOf("month");
	const endDate = currentDate.endOf("month");

	const { data: regions } = useMarketRegionList(market?.id ?? 0);
	const { data: verticals } = useVerticalListQuery();

	const { mutateAsync: bulkUpsertAsync, isPending: isSaving } =
		useBulkUpsertPartnerAvailabilityMutation();

	const {
		data: calendarResponse,
		isLoading,
		refetch,
	} = usePartnerCalendarQuery(
		{
			marketId: market?.id ?? 0,
			startDate: startDate.format("YYYY-MM-DD"),
			endDate: endDate.format("YYYY-MM-DD"),
			page: page + 1,
			limit: rowsPerPage,
			verticalIds: selectedVerticals.length > 0 ? selectedVerticals : undefined,
			regions: selectedRegions.length > 0 ? selectedRegions : undefined,
			skills:
				selectedSkills.length > 0
					? (selectedSkills as PartnerCalendarRequestDto["skills"])
					: undefined,
			sortByAvailabilityDate: sortDate,
		},
		!!market?.id,
	);

	const modificationsByPartner = useMemo(() => {
		const map = new Map<number, Map<string, PartnerCalendarStatus>>();
		for (const [key, status] of modifiedAvailability) {
			const [partnerIdStr, date] = key.split(":");
			const partnerId = Number(partnerIdStr);
			if (!map.has(partnerId)) {
				map.set(partnerId, new Map());
			}
			map.get(partnerId)?.set(date, status);
		}
		return map;
	}, [modifiedAvailability]);

	const calendarData: PartnerCalendarRowData[] = useMemo(() => {
		if (!calendarResponse?.partners) return [];

		return calendarResponse.partners.map(
			(partner: PartnerCalendarPartnerDto) => {
				const availabilityMap: Record<string, PartnerAvailability> = {};

				partner.calendar.forEach((day) => {
					availabilityMap[day.date] = {
						date: day.date,
						status: day.status as PartnerCalendarStatus,
						isBlocked: day.isBlocked,
						capacity: day.capacity,
						bookings:
							day.bookings?.map((b) => ({
								orderId: b.orderId ?? 0,
								orderPrice: b.orderPrice ?? 0,
								duration: b.duration ?? 0,
								dayOfJob: b.dayOfJob ?? 0,
							})) || [],
					};
				});

				// Merge modified availability
				const partnerModifications = modificationsByPartner.get(partner.id);
				if (partnerModifications) {
					for (const [date, status] of partnerModifications) {
						const isBlocked = status === PartnerCalendarStatus.NOT_AVAILABLE;
						if (availabilityMap[date]) {
							availabilityMap[date] = {
								...availabilityMap[date],
								status,
								isBlocked,
							};
						} else {
							availabilityMap[date] = {
								date,
								status,
								isBlocked,
								bookings: [],
								capacity: 1,
							};
						}
					}
				}

				return {
					partner: {
						id: partner.id,
						name: partner.name,
						email: partner.email,
						phone: partner.phone,
						verticals: partner.verticals.map((v) =>
							typeof v === "string"
								? { name: v, level: "Bronze" }
								: { name: v.name, level: v.level },
						),
						confirmedJobsCount: partner.confirmedJobsCount,
						inProgressJobsCount: partner.inProgressJobsCount,
					},
					availability: availabilityMap,
				};
			},
		);
	}, [calendarResponse, modificationsByPartner]);

	const handlePrevMonth = () => {
		setCurrentDate(currentDate.subtract(1, "month"));
		setSortDate(undefined);
		setPage(0);
	};
	const handleNextMonth = () => {
		setCurrentDate(currentDate.add(1, "month"));
		setSortDate(undefined);
		setPage(0);
	};

	const handleChangePage = (_event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleToggleAvailability = (
		partnerId: number,
		date: string,
		currentStatus?: PartnerCalendarStatus,
	) => {
		let newStatus: PartnerCalendarStatus;
		if (currentStatus === PartnerCalendarStatus.AVAILABLE) {
			newStatus = PartnerCalendarStatus.NOT_AVAILABLE;
		} else {
			newStatus = PartnerCalendarStatus.AVAILABLE;
		}

		setModifiedAvailability((prev) => {
			const next = new Map(prev);
			const key = `${partnerId}:${date}`;
			next.set(key, newStatus);
			return next;
		});
	};

	const handleSave = () => {
		const items: PartnerAvailabilityUpdateItemDto[] = Array.from(
			modifiedAvailability,
		).map(([key, status]) => {
			const [partnerIdStr, date] = key.split(":");
			return {
				partnerId: Number(partnerIdStr),
				date,
				status:
					status === PartnerCalendarStatus.AVAILABLE
						? "available"
						: "not_available",
			};
		});

		if (items.length === 0) return;

		toast.promise(
			bulkUpsertAsync({ items }).then(() => {
				setModifiedAvailability(new Map());
				refetch();
			}),
			{
				loading: "Updating availability...",
				success: "Availability updated successfully",
				error: "Failed to update availability",
			},
		);
	};

	const hasChanges = modifiedAvailability.size > 0;

	const handleCopyEmails = async () => {
		setIsCopyingEmails(true);
		try {
			const request: PartnerCalendarRequest = {
				marketId: market?.id ?? 0,
				startDate: startDate.format("YYYY-MM-DD"),
				endDate: endDate.format("YYYY-MM-DD"),
				page: 1,
				limit: 1,
				sortByAvailabilityDate: sortDate,
				verticalIds:
					selectedVerticals.length > 0 ? selectedVerticals : undefined,
				regions: selectedRegions.length > 0 ? selectedRegions : undefined,
				skills: selectedSkills.length > 0 ? selectedSkills : undefined,
			};
			const { emails } = await processResponse(postPartnerCalendarEmails)(
				request,
			);
			await navigator.clipboard.writeText(emails.join(", "));
			toast.success(`${emails.length} email addresses copied`);
		} catch {
			toast.error("Failed to copy email addresses");
		} finally {
			setIsCopyingEmails(false);
		}
	};

	if (isLoading) {
		return <PartnerCalendarSkeleton />;
	}

	return (
		<Stack spacing={0}>
			<Card sx={{ p: { xs: 1, md: 2 } }}>
				<Stack
					direction={{ xs: "column", md: "row" }}
					justifyContent="space-between"
					alignItems="center"
					mb={2}
					spacing={2}
				>
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						width={{ xs: "100%", md: "auto" }}
						justifyContent={{ xs: "center", md: "flex-start" }}
					>
						<IconButton onClick={handlePrevMonth}>
							<ChevronLeft />
						</IconButton>
						<Typography variant="h6">
							{currentDate.format("MMMM YYYY")}
						</Typography>
						<IconButton onClick={handleNextMonth}>
							<ChevronRight />
						</IconButton>
					</Stack>
					<Legend />
				</Stack>

				<Stack
					direction={{ xs: "column", md: "row" }}
					justifyContent="space-between"
					alignItems={{ xs: "stretch", md: "center" }}
					spacing={2}
				>
					<Stack
						direction={{ xs: "column", md: "row" }}
						spacing={2}
						flex={1}
						alignItems={{ xs: "stretch", md: "center" }}
					>
						<FormControl
							size="small"
							sx={{
								minWidth: { xs: "100%", md: 200 },
								maxWidth: { xs: "100%", md: 300 },
							}}
						>
							<InputLabel>Cities</InputLabel>
							<Select
								multiple
								value={selectedRegions}
								label="Regions"
								onChange={(e) => {
									const value = e.target.value;
									setSelectedRegions(
										typeof value === "string" ? [] : (value as number[]),
									);
								}}
								renderValue={(selected) =>
									regions
										?.filter((region) => selected.includes(region.id))
										.map((region) => region.name)
										.join(", ")
								}
							>
								{regions?.map((region) => (
									<MenuItem key={region.id} value={region.id}>
										{region.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl
							size="small"
							sx={{
								minWidth: { xs: "100%", md: 200 },
								maxWidth: { xs: "100%", md: 300 },
							}}
						>
							<InputLabel>Verticals</InputLabel>
							<Select
								multiple
								value={selectedVerticals}
								label="Verticals"
								onChange={(e) => {
									const value = e.target.value;
									setSelectedVerticals(
										typeof value === "string" ? [] : (value as number[]),
									);
								}}
								renderValue={(selected) =>
									verticals
										?.filter((v) => selected.includes(v.id))
										.map((v) => v.name)
										.join(", ")
								}
							>
								{verticals?.map((vertical) => (
									<MenuItem key={vertical.id} value={vertical.id}>
										{vertical.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl
							size="small"
							sx={{
								minWidth: { xs: "100%", md: 200 },
								maxWidth: { xs: "100%", md: 300 },
							}}
						>
							<InputLabel>Skills</InputLabel>
							<Select
								multiple
								value={selectedSkills}
								label="Skills"
								onChange={(e) => {
									const value = e.target.value;
									setSelectedSkills(
										typeof value === "string" ? [] : (value as PartnerSkills[]),
									);
								}}
								renderValue={(selected) =>
									selected.map(formatSkillLabel).join(", ")
								}
							>
								{Object.values(PartnerSkills).map((skill) => (
									<MenuItem key={skill} value={skill}>
										{formatSkillLabel(skill)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{(selectedRegions.length > 0 ||
							selectedVerticals.length > 0 ||
							selectedSkills.length > 0) && (
							<Button
								variant="outlined"
								onClick={() => {
									setSelectedRegions([]);
									setSelectedVerticals([]);
									setSelectedSkills([]);
								}}
								sx={{ whiteSpace: "nowrap" }}
							>
								Clear Filters
							</Button>
						)}
					</Stack>

					<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
						<Button
							variant="outlined"
							startIcon={
								isCopyingEmails ? (
									<CircularProgress size={20} />
								) : (
									<ContentCopy />
								)
							}
							onClick={handleCopyEmails}
							disabled={!market?.id || isCopyingEmails}
							sx={{ whiteSpace: "nowrap" }}
						>
							Copy emails
						</Button>
						<Button
							variant="contained"
							startIcon={isSaving ? <CircularProgress size={20} /> : <Save />}
							onClick={handleSave}
							disabled={!hasChanges || isSaving}
							sx={{ whiteSpace: "nowrap" }}
						>
							Save Changes
						</Button>
					</Stack>
				</Stack>
			</Card>

			<Card sx={{ p: { xs: 1, md: 2 } }}>
				<Box sx={{ overflowX: "auto" }}>
					<CalendarGrid
						startDate={startDate}
						daysCount={startDate.daysInMonth()}
						rows={calendarData}
						onToggleAvailability={handleToggleAvailability}
						sortDate={sortDate}
						onSortDateChange={(date) => {
							setSortDate(date);
							setPage(0);
						}}
					/>
				</Box>
				<TablePagination
					component="div"
					count={calendarResponse?.total ?? 0}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</Card>
		</Stack>
	);
}
